import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TemplatesService } from '../../../services/templates.service';

declare const mammoth: any;
declare const window: any;

@Component({
  selector: 'app-template-editor',
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.css']
})
export class TemplateEditorComponent implements OnInit, OnDestroy {
  templateId!: number;
  versionId!: number;
  isLoading = false;
  isSaving = false;
  htmlContent = '';
  private editorInited = false;
  loadError: string | null = null;
  private blobUrls: string[] = []; // Para limpar URLs de blob ao destruir

  constructor(private route: ActivatedRoute, public router: Router, private templatesService: TemplatesService) {}

  ngOnInit(): void {
    this.templateId = Number(this.route.snapshot.paramMap.get('id')) || 0;
    this.versionId = Number(this.route.snapshot.paramMap.get('versionId')) || 0;
    this.loadDocxAsHtml();
  }

  private loadDocxAsHtml(): void {
    this.isLoading = true;
    this.loadError = null;

    // Abordagem antiga: sempre baixar a VERSÃO específica do template para edição
    // (mantendo apenas logs e validações adicionais)

    this.templatesService.downloadVersion(this.versionId).subscribe({
      next: async ({ blob, fileName, mimeType }) => {
        
        if (!blob || blob.size === 0) {
          this.isLoading = false;
          this.loadError = 'O arquivo está vazio ou não foi encontrado.';
          return;
        }

        // Validar se o arquivo é realmente um DOCX (ZIP)
        const isValidDocx = await this.validateDocxFile(blob);
        if (!isValidDocx) {
          this.isLoading = false;
          this.loadError = 'O arquivo não é um documento DOCX válido. O arquivo pode estar corrompido ou em formato diferente.';
          return;
        }

        const reader = new FileReader();
        reader.onerror = () => {
          console.error('Erro ao ler arquivo');
          this.isLoading = false;
          this.loadError = 'Erro ao ler o arquivo. Verifique se o arquivo está corrompido.';
        };
        
        reader.onload = async () => {
          try {
            const arrayBuffer = reader.result as ArrayBuffer;
            
            // Verificar se mammoth está disponível
            const w: any = window as any;
            const mammothLib = w.mammoth || mammoth;
            
            if (!mammothLib) {
              console.error('Mammoth não está disponível');
              this.isLoading = false;
              this.loadError = 'Biblioteca de conversão não carregada. Recarregue a página.';
              return;
            }

            const result = await mammothLib.convertToHtml({ arrayBuffer });
            const html = (result?.value || '').trim();
            if (!html) {
              // Fallback 1: tentar extrair HTML a partir de word/afchunk.mht (altChunk) dentro do DOCX
              const extracted = await this.tryExtractHtmlFromDocx(arrayBuffer);
              if (extracted && extracted.trim()) {
                this.htmlContent = extracted;
                this.initTiny();
              } else {
                // Fallback 2: tentar abrir como HTML puro caso o arquivo esteja salvo como texto/html
                const asText = await this.tryReadBlobAsText(blob);
                const looksLikeHtml = asText && /<\s*html|<\s*body|<\s*p|<\s*div/i.test(asText);
                if (looksLikeHtml) {
                  this.htmlContent = asText;
                  this.initTiny();
                } else {
                  console.error('Não foi possível converter o arquivo');
                  this.loadError = 'Não foi possível converter este .docx para edição no navegador. O arquivo pode estar corrompido ou em formato não suportado.';
                }
              }
            } else {
              this.htmlContent = html;
              this.initTiny();
            }
          } catch (error) {
            console.error('Erro durante conversão:', error);
            this.isLoading = false;
            
            // Mensagem de erro mais específica
            let errorMessage = 'Erro ao processar o arquivo.';
            if (error instanceof Error) {
              if (error.message.includes('zip file') || error.message.includes('central directory')) {
                errorMessage = 'O arquivo não é um DOCX válido. O arquivo pode estar corrompido ou não ser um documento Word.';
              } else {
                errorMessage = `Erro ao processar: ${error.message}`;
              }
            }
            
            this.loadError = errorMessage;
          } finally {
            this.isLoading = false;
          }
        };
        reader.readAsArrayBuffer(blob);
      },
      error: (error) => {
        console.error('Erro ao baixar versão:', error);
        this.isLoading = false;
        this.loadError = `Falha ao carregar a versão do documento: ${error.message || 'Erro desconhecido'}`;
      }
    });
  }

  private async validateDocxFile(blob: Blob): Promise<boolean> {
    try {
      // Verificar se o arquivo começa com a assinatura ZIP (PK)
      const firstBytes = await blob.slice(0, 4).arrayBuffer();
      const view = new Uint8Array(firstBytes);
      const signature = String.fromCharCode(view[0], view[1]);
      
      // DOCX é um ZIP, então deve começar com "PK" (0x50 0x4B)
      if (signature !== 'PK') {
        console.error('Arquivo não é um ZIP válido. Assinatura:', signature);
        return false;
      }
      
      // Tentar abrir como ZIP para validar
      const w: any = window as any;
      const JSZip = w.JSZip;
      if (JSZip) {
        const arrayBuffer = await blob.arrayBuffer();
        try {
          await JSZip.loadAsync(arrayBuffer);
          return true;
        } catch (zipError) {
          console.error('Erro ao validar ZIP:', zipError);
          return false;
        }
      }
      
      // Se JSZip não estiver disponível, confiar na assinatura
      return true;
    } catch (error) {
      console.error('Erro ao validar arquivo:', error);
      return false;
    }
  }

  private async tryReadBlobAsText(blob: Blob): Promise<string> {
    try {
      const text = await new Response(blob).text();
      return (text || '').trim();
    } catch {
      return '';
    }
  }

  private async tryExtractHtmlFromDocx(arrayBuffer: ArrayBuffer): Promise<string> {
    try {
      const w: any = window as any;
      const JSZip = w.JSZip || (w as any).jszip || (w as any).JSZip;
      if (!JSZip) return '';
      const zip = await JSZip.loadAsync(arrayBuffer);
      // O html-docx-js normalmente embute o HTML principal em word/afchunk.mht
      const mhtFile = zip.file('word/afchunk.mht');
      if (!mhtFile) return '';
      const mhtText = await mhtFile.async('string');
      
      // Extrair imagens e HTML do MHTML
      const { html, images } = this.parseMhtmlContent(mhtText);
      
      // Substituir referências de imagens no HTML por URLs de blob
      let processedHtml = html;
      for (const [src, blobUrl] of Object.entries(images)) {
        // Substituir tanto src quanto href que apontam para a imagem
        processedHtml = processedHtml.replace(
          new RegExp(`(src|href)=["']${this.escapeRegExp(src)}["']`, 'gi'),
          `$1="${blobUrl}"`
        );
      }
      
      return processedHtml.trim();
    } catch {
      return '';
    }
  }

  private parseMhtmlContent(mhtText: string): { html: string; images: Record<string, string> } {
    const images: Record<string, string> = {};
    let html = '';
    
    // Encontrar o boundary do MHTML
    const boundaryMatch = mhtText.match(/boundary="([^"]+)"/);
    if (!boundaryMatch) return { html: '', images };
    
    const boundary = boundaryMatch[1];
    const parts = mhtText.split(`--${boundary}`);
    
    for (const part of parts) {
      if (part.includes('Content-Type: text/html')) {
        // Extrair HTML
        const htmlMatch = part.match(/Content-Type:\s*text\/html[\s\S]*?\r?\n\r?\n([\s\S]*?)(?=\r?\n--|$)/i);
        if (htmlMatch) {
          let htmlContent = htmlMatch[1].trim();
          
          // Detectar Content-Transfer-Encoding
          const encMatch = part.match(/Content-Transfer-Encoding:\s*([^\r\n]+)/i);
          const encoding = encMatch ? encMatch[1].trim().toLowerCase() : '';
          
          if (encoding === 'quoted-printable') {
            htmlContent = this.decodeQuotedPrintable(htmlContent);
          }
          
          html = htmlContent;
        }
      } else if (part.includes('Content-Type: image/')) {
        // Extrair imagens
        const contentTypeMatch = part.match(/Content-Type:\s*([^;\r\n]+)/i);
        const locationMatch = part.match(/Content-Location:\s*([^\r\n]+)/i);
        
        if (contentTypeMatch && locationMatch) {
          const contentType = contentTypeMatch[1].trim();
          const contentLocation = locationMatch[1].trim();
          
          // Extrair dados base64
          const base64Match = part.match(/\r?\n\r?\n([A-Za-z0-9+/=]+)/);
          if (base64Match) {
            try {
              const base64Data = base64Match[1].replace(/\r?\n/g, '');
              const binaryString = atob(base64Data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              
              const blob = new Blob([bytes], { type: contentType });
              const blobUrl = URL.createObjectURL(blob);
              images[contentLocation] = blobUrl;
              this.blobUrls.push(blobUrl); // Registrar para limpeza posterior
            } catch (e) {
              // Ignorar falha ao processar imagem individual
            }
          }
        }
      }
    }
    
    return { html, images };
  }

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private decodeQuotedPrintable(input: string): string {
    // Remove soft line breaks =\r?\n e converte =XX para bytes
    const cleaned = input.replace(/=\r?\n/g, '');
    return cleaned.replace(/=([0-9A-Fa-f]{2})/g, (_m, hex) => String.fromCharCode(parseInt(hex, 16)));
  }

  private initTiny(): void {
    if (this.editorInited) return;
    const self = this;
    setTimeout(() => {
      const w: any = window as any;
      if (!w.tinymce) return;
      w.tinymce.init({
        selector: '#docxEditor',
        api_key: 'euip07pslftzv0nt86kjhzuc20huyn22dyybgpybfet1iv7s',
        height: 600,
        menubar: true,
        paste_data_images: false,
        cleanup: true,
        convert_fonts_to_spans: false,
        remove_trailing_brs: true,
        forced_root_block: 'p',
        valid_styles: {
          '*': 'text-align,color,background-color,font-size,font-weight,font-style,text-decoration'
        },
        invalid_styles: 'line-height,letter-spacing,word-spacing',
        invalid_elements: 'script,style,iframe,object,embed',
        plugins: 'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
        toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | table | removeformat | preview fullscreen | help',
        branding: false,
        setup(editor: any) {
          editor.on('init', () => {
            try {
              editor.setContent(self.htmlContent || '');
              if (editor.mode?.set) {
                editor.mode.set('design');
              }
              editor.setMode?.('design');
              editor.getBody()?.setAttribute('contenteditable', 'true');
              editor.focus();
            } catch {}
          });
          editor.on('focus', () => {
            try {
              if (editor.mode?.set) editor.mode.set('design');
              editor.getBody()?.setAttribute('contenteditable', 'true');
            } catch {}
          });
        }
      });
      this.editorInited = true;
    }, 0);
  }

  async save(): Promise<void> {
    if (!this.htmlContent) return;
    this.isSaving = true;
    try {
      const w: any = window as any;
      const editor = w.tinymce?.get('docxEditor');
      const currentHtml = editor ? editor.getContent({ format: 'html' }) : this.htmlContent;
      const cleanedHtml = this.sanitizeHtml(currentHtml);
      const HtmlDocx = w.htmlDocx;
      const fullHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"></head><body>${cleanedHtml}</body></html>`;
      const docxBlob: Blob = HtmlDocx.asBlob(fullHtml);
      const fileName = `template_${this.templateId}_edited.docx`;
      const file = new File([docxBlob], fileName, { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      await this.templatesService.uploadDocx(this.templateId, file, fileName).toPromise();
      this.router.navigate(['/documents/templates', this.templateId]);
    } catch (e) {
      // erro já tratado pelo errorService no service
    } finally {
      this.isSaving = false;
    }
  }

  downloadCurrent(): void {
    this.templatesService.downloadVersion(this.versionId).subscribe(({ blob, fileName, mimeType }) => {
      const url = URL.createObjectURL(new Blob([blob], { type: mimeType }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || `template_${this.templateId}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });
  }

  cancel(): void {
    this.router.navigate(['/documents/templates', this.templateId]);
  }
  private sanitizeHtml(html: string): string {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT, null);
      const toStripAttrs = ['style', 'class', 'id', 'data-*'];
      while (walker.nextNode()) {
        const el = walker.currentNode as HTMLElement;
        // Remove estilos pesados e classes
        el.removeAttribute('style');
        el.removeAttribute('class');
        el.removeAttribute('id');
        // Remove atributos data-*
        Array.from(el.attributes)
          .filter(a => a.name.startsWith('data-'))
          .forEach(a => el.removeAttribute(a.name));
      }
      // Remove tags vazias redundantes
      doc.querySelectorAll('span,div').forEach((n: Element) => {
        if (!n.textContent?.trim() && n.children.length === 0) {
          n.remove();
        }
      });
      return doc.body.innerHTML;
    } catch {
      return html;
    }
  }

  ngOnDestroy(): void {
    const w: any = window as any;
    const editor = w.tinymce?.get('docxEditor');
    if (editor) {
      editor.remove();
    }
    this.editorInited = false;
    
    // Limpar URLs de blob para evitar vazamentos de memória
    this.blobUrls.forEach(url => URL.revokeObjectURL(url));
    this.blobUrls = [];
  }
}


